import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Apple, XCircle, Pill, Dumbbell, HomeIcon, StretchHorizontal, Users, ExternalLink, Sparkles, RefreshCw, BookmarkPlus, Download,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import ShareMenu from '@/components/ShareMenu';
import { useAuth } from '@/contexts/AuthContext';
import { reportsApi } from '@/lib/api';
import { toast } from 'sonner';
import { RESULTS } from '@/constants/testIds';
import { downloadReportPdf } from '@/lib/pdf';
import ExerciseSketch, { pickSketch } from '@/components/ExerciseSketch';
import AnimatedExerciseModal from '@/components/AnimatedExerciseModal';

const YOUNGEVITY_URL = 'https://doduro.youngevity.com/ca_en/healthy-body-start-pak-trade-2-5.html';

const SectionCard = ({ icon: Icon, title, accent = '#4A7C59', children, testId, className = '' }) => (
  <div className={`bg-white border border-[#E5E1D8] rounded-2xl p-6 sm:p-8 ${className}`} data-testid={testId}>
    <div className="flex items-center gap-3 mb-5">
      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${accent}14` }}>
        <Icon className="w-5 h-5" strokeWidth={1.5} style={{ color: accent }} />
      </div>
      <h3 className="font-serif-display text-2xl text-[#1A2421] font-medium">{title}</h3>
    </div>
    <div>{children}</div>
  </div>
);

const Item = ({ name, sub, why }) => (
  <li className="py-3 border-b border-[#E5E1D8] last:border-b-0">
    <div className="flex items-baseline justify-between gap-3">
      <span className="font-medium text-[#1A2421]">{name}</span>
      {sub && <span className="text-xs text-[#7E9979] tracking-wide font-medium whitespace-nowrap">{sub}</span>}
    </div>
    {why && <p className="text-sm text-[#4A5A53] mt-1 font-light leading-relaxed">{why}</p>}
  </li>
);

const ExerciseItem = ({ name, sub, why, tone = 'workout', onPlay }) => (
  <li className="py-3 border-b border-[#E5E1D8] last:border-b-0 flex gap-4">
    <ExerciseSketch name={name} tone={tone} onClick={() => onPlay?.({ name, sub, why, tone })} />
    <div className="flex-1 min-w-0">
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-medium text-[#1A2421]">{name}</span>
        {sub && <span className="text-xs text-[#7E9979] tracking-wide font-medium whitespace-nowrap">{sub}</span>}
      </div>
      {why && <p className="text-sm text-[#4A5A53] mt-1 font-light leading-relaxed">{why}</p>}
      <button
        type="button"
        onClick={() => onPlay?.({ name, sub, why, tone })}
        className="mt-1 text-[11px] tracking-[0.15em] uppercase font-semibold text-[#4A7C59] hover:text-[#3E6B4F]"
        data-testid={`exercise-play-${name.toLowerCase().replace(/\s+/g, '-').slice(0, 30)}`}
      >
        ▶ Tap to see the form
      </button>
    </div>
  </li>
);

const Results = () => {
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    const raw = sessionStorage.getItem('wellth_latest_report');
    if (!raw) { nav('/intake'); return; }
    try {
      const parsed = JSON.parse(raw);
      setData(parsed);
      if (parsed?.user_id) setSavedId(parsed.id);
    } catch { nav('/intake'); }
  }, [nav]);

  const attach = async () => {
    if (!data) return;
    setSaving(true);
    try {
      const saved = await reportsApi.attach({ intake: data.intake, report: data.report });
      setSavedId(saved.id);
      toast.success('Saved to your history');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save right now');
    } finally { setSaving(false); }
  };

  if (!data) return null;
  const r = data.report;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-8 py-12 sm:py-16" data-testid={RESULTS.container}>
      {/* Hero greeting */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4 text-[#7E9979]" strokeWidth={1.5} />
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#7E9979]">Your Wellth plan</span>
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-tight text-[#1A2421] font-medium max-w-3xl">
          {r.greeting}
        </h1>

        <div className="mt-8 flex flex-wrap gap-3 items-center">
          <Button
            onClick={() => { try { downloadReportPdf(data); toast.success('PDF downloaded'); } catch (e) { toast.error('Could not create PDF'); } }}
            className="rounded-full bg-[#4A7C59] hover:bg-[#3E6B4F] text-white gap-2 px-5"
            data-testid="results-download-pdf-button"
          >
            <Download className="w-4 h-4" strokeWidth={1.5} /> Download PDF
          </Button>
          <ShareMenu label="Share this plan" testId={RESULTS.shareButton} />
          {!user && !savedId && (
            <Link to="/signup">
              <Button
                variant="outline"
                className="rounded-full border-[#4A7C59] text-[#4A7C59] hover:bg-[#4A7C59] hover:text-white gap-2 px-5"
                data-testid={RESULTS.saveAccountButton}
              >
                <BookmarkPlus className="w-4 h-4" strokeWidth={1.5} /> Save to my history
              </Button>
            </Link>
          )}
          {user && !savedId && (
            <Button
              onClick={attach}
              disabled={saving}
              variant="outline"
              className="rounded-full border-[#4A7C59] text-[#4A7C59] hover:bg-[#4A7C59] hover:text-white gap-2 px-5"
              data-testid={RESULTS.saveAccountButton}
            >
              <BookmarkPlus className="w-4 h-4" strokeWidth={1.5} /> {saving ? 'Saving…' : 'Save to my history'}
            </Button>
          )}
          {savedId && (
            <span className="text-sm text-[#7E9979] font-medium">✓ Saved to your history</span>
          )}
          <Link to="/intake">
            <Button variant="ghost" className="rounded-full text-[#4A5A53] hover:text-[#4A7C59] gap-2" data-testid={RESULTS.newReportButton}>
              <RefreshCw className="w-4 h-4" strokeWidth={1.5} /> New intake
            </Button>
          </Link>
        </div>
      </motion.div>

      {/* Bento grid */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-6 mt-12">
        <SectionCard icon={Apple} title="Foods that heal you" accent="#7E9979" testId="results-good-foods" className="md:col-span-3">
          <ul>
            {(r.good_foods || []).map((f, i) => (
              <Item key={i} name={f.name} why={f.why} />
            ))}
          </ul>
        </SectionCard>

        <SectionCard icon={XCircle} title="Foods to avoid" accent="#C05746" testId="results-bad-foods" className="md:col-span-3">
          <ul>
            {(r.bad_foods || []).map((f, i) => (
              <Item key={i} name={f.name} why={f.why} />
            ))}
          </ul>
        </SectionCard>

        <SectionCard icon={Pill} title="Your supplement stack" accent="#4A7C59" testId="results-supplements" className="md:col-span-4">
          <p className="text-sm text-[#4A5A53] mb-4 font-light">Dosages tuned to your body weight. Rooted in the 90 essential nutrients philosophy.</p>
          <ul>
            {(r.supplements || []).map((s, i) => (
              <Item key={i} name={s.name} sub={s.dosage} why={s.why} />
            ))}
          </ul>
          <a
            href={YOUNGEVITY_URL}
            target="_blank"
            rel="noreferrer"
            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#4A7C59] hover:text-[#3E6B4F] underline underline-offset-4"
            data-testid={RESULTS.shopSupplementsLink}
          >
            Shop 90 For Life on Youngevity <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
          </a>
        </SectionCard>

        <SectionCard icon={Users} title="The household principle" accent="#7E9979" testId="results-household" className="md:col-span-2">
          <p className="text-[#1A2421] leading-relaxed font-light">{r.household_tip}</p>
        </SectionCard>

        <SectionCard icon={HomeIcon} title="At-home workouts" accent="#4A7C59" testId="results-home-exercises" className="md:col-span-3">
          <ul>
            {(r.home_exercises || []).map((e, i) => (
              <ExerciseItem key={i} name={e.name} sub={e.sets_reps} why={e.why} tone="workout" onPlay={setPlaying} />
            ))}
          </ul>
        </SectionCard>

        <SectionCard icon={Dumbbell} title="At the gym" accent="#4A7C59" testId="results-gym-exercises" className="md:col-span-3">
          <ul>
            {(r.gym_exercises || []).map((e, i) => (
              <ExerciseItem key={i} name={e.name} sub={e.sets_reps} why={e.why} tone="workout" onPlay={setPlaying} />
            ))}
          </ul>
        </SectionCard>

        <SectionCard icon={StretchHorizontal} title="Stretching & mobility" accent="#7E9979" testId="results-stretching" className="md:col-span-6">
          <ul className="grid sm:grid-cols-2 gap-x-8">
            {(r.stretching_mobility || []).map((s, i) => (
              <ExerciseItem key={i} name={s.name} sub={s.duration} why={s.why} tone="stretch" onPlay={setPlaying} />
            ))}
          </ul>
        </SectionCard>
      </div>

      {/* Closing encouragement */}
      <div className="mt-14 border-t border-[#E5E1D8] pt-10">
        <p className="font-serif-display text-3xl sm:text-4xl text-[#1A2421] font-medium italic leading-tight max-w-3xl">
          &ldquo;{r.closing_encouragement}&rdquo;
        </p>
        <p className="mt-4 text-xs tracking-[0.2em] uppercase font-semibold text-[#4A5A53]">— Wellth · Health is the greatest wealth</p>
      </div>

      <AnimatedExerciseModal
        open={!!playing}
        onOpenChange={(v) => !v && setPlaying(null)}
        name={playing?.name}
        sub={playing?.sub}
        why={playing?.why}
        tone={playing?.tone}
        Sketch={playing ? pickSketch(playing.name) : null}
      />
    </div>
  );
};

export default Results;
